import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { motion } from "framer-motion";
import { getStates } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { SectionHeading } from "@/components/home/ExploreByState";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const slugify = (n) => n.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

export default function Atlas() {
  const [valid, setValid] = useState({});
  const [hover, setHover] = useState(null);
  const [tip, setTip] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    getStates().then((d) => {
      const map = {};
      d.states.forEach((s) => { map[s.geo_id] = s; });
      setValid(map);
    });
  }, []);

  return (
    <div className="bg-parchment">
      <Seo
        title="Interactive Atlas of America"
        path="/atlas"
        description="Explore the United States on an interactive map. Click any state to open its full USAPulse profile — history, people, economy, nature and more."
      />
      <div className="mx-auto max-w-[1500px] px-4 py-12">
        <SectionHeading kicker="The USAPulse Atlas" title="Explore America on the Map" />
        <p className="-mt-4 mb-6 max-w-2xl text-muted-foreground">
          <T>Click any state to open its full profile — history, people, economy, nature and more. This is the visual navigation system for the entire USAPulse network.</T>
        </p>

        <div className="relative overflow-hidden rounded-2xl border border-navy/15 bg-white shadow-xl">
          <ComposableMap projection="geoAlbersUsa" width={980} height={560} style={{ width: "100%", height: "auto" }}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties.name;
                  const slug = slugify(name);
                  const clickable = !!valid[slug];
                  const isHover = hover === slug;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => setHover(slug)}
                      onMouseLeave={() => setHover(null)}
                      onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY })}
                      onClick={() => clickable && navigate(`/place/us/${slug}`)}
                      data-testid={`atlas-state-${slug}`}
                      style={{
                        default: { fill: isHover ? "#c8102e" : "#e8dcc4", stroke: "#0a2a5e", strokeWidth: 0.6, outline: "none", transition: "fill 0.2s" },
                        hover: { fill: "#c8102e", stroke: "#0a2a5e", strokeWidth: 0.8, outline: "none", cursor: clickable ? "pointer" : "default" },
                        pressed: { fill: "#a10d25", outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          {hover && valid[hover] && (
            <div
              className="pointer-events-none fixed z-50 rounded-md bg-navy px-3 py-1.5 text-sm font-semibold text-white shadow-lg"
              style={{ left: tip.x + 14, top: tip.y + 14 }}
            >
              {valid[hover].official_name}
              <span className="ml-2 text-xs font-normal text-white/70">{valid[hover].nickname}</span>
            </div>
          )}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-2"><span className="inline-block h-3 w-3 rounded-sm bg-[#e8dcc4] ring-1 ring-navy/40" /> <T>State</T></span>
          <span className="flex items-center gap-2"><span className="inline-block h-3 w-3 rounded-sm bg-oldglory-red" /> <T>Hover to preview · click to open</T></span>
        </motion.div>
      </div>
    </div>
  );
}
